import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const BTN_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";
const BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const CANCEL_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.08)";
const CANCEL_BG =
  "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), #F5F5F5";

const btnTextSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  textTransform: "none",
};

const readOnlyFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    borderRadius: "4px",
    bgcolor: "#F5F5F5",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    color: "#000000",
    "& fieldset": { borderColor: "#E6E6E6", borderWidth: 1 },
    "&:hover fieldset": { borderColor: "#E6E6E6" },
    "&.Mui-focused fieldset": { borderColor: "#E6E6E6", borderWidth: 1 },
  },
  "& .MuiInputBase-input": {
    py: "8px",
    px: "10px",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    color: "#000000",
    cursor: "default",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
};

const descriptionFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    display: "flex",
    alignItems: "stretch",
    boxSizing: "border-box",
    minHeight: 58,
    padding: "8px 10px",
    overflow: "hidden",
    resize: "vertical",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    color: "#000000",
    boxShadow: "none !important",
    filter: "none",
    "& fieldset": {
      borderColor: "#707070",
      borderWidth: "1px !important",
      boxShadow: "none",
      pointerEvents: "none",
    },
    "&:hover fieldset": { borderColor: "#707070", borderWidth: "1px !important" },
    "&.Mui-focused": { boxShadow: "none !important", filter: "none" },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
      borderWidth: "1px !important",
      boxShadow: "none",
    },
  },
  "& .MuiInputBase-input, & .MuiOutlinedInput-input, & textarea": {
    boxSizing: "border-box",
    flex: 1,
    alignSelf: "stretch",
    width: "100% !important",
    maxWidth: "100%",
    height: "100% !important",
    minHeight: 0,
    margin: 0,
    padding: "0 !important",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    letterSpacing: 0,
    color: "#000000",
    whiteSpace: "pre-wrap",
    overflowWrap: "normal",
    wordBreak: "normal",
    boxShadow: "none",
    textShadow: "none",
    filter: "none",
    outline: "none",
    appearance: "none",
    resize: "none",
    overflow: "auto",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#98A2B3",
    opacity: 1,
  },
};

function RequiredMark() {
  return (
    <Typography component="span" sx={{ color: "#DE350B", mr: 0.25, fontWeight: 700 }}>
      *
    </Typography>
  );
}

function FieldLabel({ children, required }) {
  return (
    <Typography
      sx={{
        fontFamily: "Lato, sans-serif",
        fontSize: 14,
        fontWeight: 700,
        lineHeight: "21px",
        letterSpacing: 0,
        color: "#707070",
        mb: "8px",
      }}
    >
      {required ? <RequiredMark /> : null}
      {children}
    </Typography>
  );
}

export default function ModifiedGeneratedStrategyDrawer({
  open,
  onClose,
  onSave,
  subsectionHeading = "",
  cardTitle = "",
  initialDescription = "",
}) {
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setDescription(initialDescription || "");
  }, [open, initialDescription]);

  const canSave = useMemo(() => Boolean(description.trim()), [description]);

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSubmitting(true);
      await onSave({ description: description.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          maxWidth: "100vw",
          height: "100%",
          bgcolor: "#FFFFFF",
          boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.14)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#FFFFFF", minWidth: 0 }}>
        <Box
          sx={{
            flexShrink: 0,
            boxSizing: "border-box",
            minHeight: 88,
            pl: { xs: 2, sm: "24px" },
            pr: { xs: 2, sm: "16px" },
            py: "16px",
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #E6E6E6",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px",
                letterSpacing: 0,
                color: "#555555",
              }}
            >
              Modified Generated Strategy
            </Typography>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "24px",
                letterSpacing: 0,
                color: "#555555",
              }}
            >
              Modify strategy details.
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{
              width: 24,
              height: 24,
              p: 0,
              color: "#555555",
              flexShrink: 0,
              "& .MuiSvgIcon-root": { fontSize: 16 },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "scroll",
            overflowX: "hidden",
            px: { xs: 2, sm: "16px" },
            py: { xs: 2, sm: "24px" },
            bgcolor: "#FFFFFF",
            scrollbarGutter: "stable",
            scrollbarWidth: "auto",
            scrollbarColor: "#C1C1C1 #FAFAFA",
            "&::-webkit-scrollbar": {
              width: 14,
              height: 14,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#FAFAFA",
              border: "1px solid #E8E8E7",
              borderRadius: 0,
            },
            "&::-webkit-scrollbar-thumb": {
              boxSizing: "border-box",
              width: 8,
              minHeight: 24,
              backgroundColor: "#C1C1C1",
              borderRadius: "4px",
              borderTop: "2px solid #FAFAFA",
              borderBottom: "2px solid #FAFAFA",
              borderLeft: "3px solid #FAFAFA",
              borderRight: "3px solid #FAFAFA",
              backgroundClip: "padding-box",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#C1C1C1",
            },
            "&::-webkit-scrollbar-button": {
              display: "none",
              width: 0,
              height: 0,
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: "#FAFAFA",
            },
          }}
        >
          <Stack spacing="16px" sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel>Strategy Name</FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={subsectionHeading}
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            </Box>

            {cardTitle ? (
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>Subsection Name</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={cardTitle}
                  InputProps={{ readOnly: true }}
                  sx={readOnlyFieldSx}
                />
              </Box>
            ) : null}

            <Box sx={{ minWidth: 0 }}>
              <FieldLabel required>Description</FieldLabel>
              <TextField
                fullWidth
                multiline
                minRows={1}
                placeholder="Describe the testing methodology, expected outcomes, and acceptance criteria..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={descriptionFieldSx}
              />
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            boxSizing: "border-box",
            width: "100%",
            minHeight: 88,
            px: { xs: 2, sm: "24px" },
            py: { xs: 2, sm: "24px" },
            bgcolor: "#F5F5F5",
            borderTop: "1px solid #E6E6E6",
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 1.5, sm: "16px" },
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontSize: 13,
              lineHeight: "24px",
              letterSpacing: 0,
              color: "#555555",
              flex: { sm: "1 1 310px" },
              minWidth: 0,
            }}
          >
            Changes saved automatically.
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "12px",
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              disableElevation
              disableRipple
              variant="outlined"
              onClick={onClose}
              disabled={submitting}
              sx={{
                ...btnTextSx,
                boxSizing: "border-box",
                height: 40,
                minHeight: 40,
                minWidth: 0,
                width: "fit-content",
                px: "16px",
                py: "8px",
                gap: "4px",
                borderRadius: "4px",
                border: "1px solid #BBBBBB",
                borderColor: "#BBBBBB",
                backgroundColor: "#F5F5F5",
                backgroundImage: CANCEL_BG,
                color: "#555555",
                boxShadow: CANCEL_SHADOW,
                whiteSpace: "nowrap",
                verticalAlign: "middle",
                justifyContent: "center",
                alignItems: "center",
                "&:hover": {
                  border: "1px solid #BBBBBB",
                  borderColor: "#BBBBBB",
                  backgroundColor: "#F5F5F5",
                  backgroundImage: CANCEL_BG,
                  color: "#555555",
                  boxShadow: CANCEL_SHADOW,
                },
                "&:focus, &:focus-visible": {
                  border: "1px solid #BBBBBB",
                  borderColor: "#BBBBBB",
                  backgroundColor: "#F5F5F5",
                  backgroundImage: CANCEL_BG,
                  color: "#555555",
                  boxShadow: CANCEL_SHADOW,
                },
                "&.Mui-disabled": {
                  border: "1px solid #BBBBBB",
                  backgroundColor: "#F5F5F5",
                  backgroundImage: CANCEL_BG,
                  color: "#555555",
                  opacity: 0.5,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              disableRipple
              variant="contained"
              onClick={handleSave}
              disabled={!canSave || submitting}
              sx={{
                ...btnTextSx,
                height: 40,
                minHeight: 40,
                minWidth: 0,
                width: "fit-content",
                px: "16px",
                py: "8px",
                gap: "4px",
                borderRadius: "4px",
                border: "1px solid transparent",
                color: "#FFFFFF",
                backgroundImage: PRIMARY_BG,
                boxShadow: BTN_SHADOW,
                "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
                  backgroundImage: PRIMARY_BG_HOVER,
                  boxShadow: BTN_SHADOW,
                },
                "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
                  outline: "none",
                  color: "#FFFFFF",
                  bgcolor: "transparent !important",
                  backgroundColor: "transparent !important",
                  backgroundImage: `${PRIMARY_BG} !important`,
                  border: "2px solid #005FCC",
                  boxShadow: BTN_SHADOW,
                },
                "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
                  bgcolor: "transparent !important",
                  backgroundColor: "transparent !important",
                  backgroundImage: `${PRIMARY_BG} !important`,
                  border: "2px solid #005FCC",
                },
                "&:active:not(.Mui-disabled)": {
                  color: "#FFFFFF",
                  bgcolor: "transparent !important",
                  backgroundColor: "transparent !important",
                  backgroundImage: `${PRIMARY_BG_ACTIVE} !important`,
                  border: "1px solid transparent",
                  boxShadow: BTN_INSET,
                },
                "& .MuiTouchRipple-root": { display: "none" },
                "&.Mui-disabled": {
                  color: "#BBBBBB",
                  bgcolor: "#F5F5F5",
                  backgroundColor: "#F5F5F5",
                  backgroundImage: "none",
                  border: "1px solid #E6E6E6",
                  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
                  opacity: 1,
                  cursor: "not-allowed",
                },
              }}
            >
              Update Strategy
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
